import React from 'react';
import classNames from 'classnames';

import useApi from 'api';

import Dialog from 'ui/dialog';
import Tooltip from 'ui/tooltip';
import { OverlayContext } from 'ui/utils/OverlayManager';
import accountActions from './accountActions';

function validateLength(name, minLength, maxLength, value) {
  if (value.length < minLength || value.length > maxLength) {
    return `Length of ${name} must be between ${minLength} and ${maxLength}.`;
  }
}
function validateRegexp(message, regexp, value) {
  if (!value.match(regexp)) {
    return (message);
  }
}
const makeValidator = (...args) => function(value, action) {
  for (const validator of args) {
    const error = validator(value, action);
    if (error === true) return;
    if (error) return error;
  }
};

const fieldOrder = ['name', 'reset_code', 'password_old', 'email', 'password', 'password_repeat', 'remember'];
const buttonOrder = ['signin', 'register', 'recover', 'reset', 'update', 'cancel', 'lostpassword', 'already', 'delete'];

const formFields = {
  name: {
    type: "text",
    validator: makeValidator(
      validateLength.bind(null, "username", 2, 16),
      validateRegexp.bind(null, "Username may only consist of a-z and 0-9.", /^[a-z0-9._]+$/i),
    ),
  },
  email: {
    type: "text",
    validator: makeValidator(
      (value, action) => action !== "recover" && !value.length,
      validateLength.bind(null, "e-mail", 4, 128),
      validateRegexp.bind(null, "Specify a valid e-mail address", /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/),
    ),
  },
  password: {
    type: "password",
    validator: makeValidator(
      (value, action) => action === "options" && !value.length,
      validateLength.bind(null, "password", 6, 64),
    ),
  },
  password_repeat: {
    type: "password",
    validator(value, action, params) {
      if (value !== params.password) return `Passwords do not match.`;
      if (!value) return formFields.password.validator("", action, params);
    },
  },
  remember: {
    type: "checkbox",
  },
  reset_code: {
    type: "text",
    validator: validateRegexp.bind(null, "Invalid reset code", /^[0-9a-f]{32}$/),
  },
  password_old: {
    type: "password",
    validator: validateLength.bind(null, "password", 6, 64),
  },
};

function initialState(action, initialParams) {
  const result = {};
  const formData = accountActions[action];
  for (const field in formData.fields) {
    switch (formFields[field]?.type) {
    case "text":
    case "password":
      result[field] = (initialParams && initialParams[field]) || "";
      break;
    case "checkbox":
      result[field] = (initialParams && initialParams[field]) || false;
      break;
    // no default
    }
  }
  return result;
}

function TextInput({ name, type, validator, value, onChange }) {
  const error = validator(value);
  return (
    <Tooltip tooltip={value ? error : `Required field`} disabled={!error}>
      <input className={classNames("lap-input", {"lap-error": error && value})} type={type} value={value} onChange={onChange} name={name}/>
    </Tooltip>
  );
}

function DeleteConfirmation({ onResolve, onClose }) {
  return (
    <Dialog title={`Delete account`} modal onClose={onClose}>
      <Dialog.Content>
        <p>{`Your LAPlanner account will be PERMANENTLY deleted, along with all profiles saved under your account. Continue?`}</p>
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={() => onResolve(true)}>{`Yes`}</button>
        <button onClick={() => onResolve(false)}>{`No`}</button>
      </Dialog.Footer>
    </Dialog>
  );
}

export default function AccountDialog({ action: initialAction, params: initialParams, onClose }) {
  const [action, setAction] = React.useState(initialAction);
  const [values, setValues] = React.useState(() => initialState(action, initialParams));
  const [result, setResult] = React.useState(null);
  const [resultFlash, setResultFlash] = React.useState(false);

  const api = useApi();
  const formData = accountActions[action];

  React.useEffect(() => {
    if (result) {
      setResultFlash(true);
      const handle = setTimeout(() => setResultFlash(false), 500);
      return () => clearTimeout(handle);
    }
  }, [result]);

  function setValue(field, value) {
    setResult(null);
    setValues(values => ({...values, [field]: value}));
  }

  const overlays = React.useContext(OverlayContext);
  const onClick = React.useCallback(async button => {
    switch (button) {
    case 'cancel':
      onClose();
      break;
    case 'lostpassword':
      setAction('recover');
      setValues(initialState('recover'));
      break;
    case 'already':
      setAction('reset');
      setValues(initialState('reset'));
      break;
    case 'delete':
      {
        const error = formFields.password_old.validator?.(values.password_old, action, values);
        if (error) {
          setResult(values.password_old ? error : `Missing required fields`);
          return;
        }
        if (await overlays.async(<DeleteConfirmation/>)) {
          api.account('delete', {password_old: values.password_old}).then(() => {
            onClose();
          }, error => setResult(error.message));
        }
      }
      break;
    default:
      for (const field in values) {
        const error = formFields[field].validator?.(values[field], action, values);
        if (error) {
          setResult(values[field] ? error : `Missing required fields`);
          return;
        }
      }
      api.account(action, values).then(() => {
        if (action === 'options' || action === 'recover') {
          setResult(action === 'recover' ? `Password reset link sent.` : `Settings updated.`);
          setValues(initialState(action));
        } else {
          onClose();
        }
      }, error => setResult(error.message));
    }
  }, [action, api, onClose, values, overlays]);

  const onKeyDown = React.useCallback(e => {
    if (e.key === "Enter") {
      for (const button of buttonOrder) {
        if (formData.buttons[button]) {
          onClick(button);
          break;
        }
      }
    }
  }, [formData, onClick]);

  return (
    <Dialog className="lap-AccountDialog" title={formData.title} modal onClose={onClose} onKeyDown={onKeyDown}>
      <Dialog.Content>
        <p className={classNames("lap-tip", {"lap-highlight": resultFlash})}>
          {result || formData.tip}
        </p>
        <form>
          {fieldOrder.map(field => {
            const name = formData.fields[field];
            if (!name) return null;
            const info = formFields[field];
            if (!info) return null;
            if (info.type === "text" || info.type === "password") {
              const required = info.validator("", action, values);
              return (
                <label key={field}>
                  {name}: {required ? <span title={`Required field`}>*</span> : null}
                  <TextInput type={info.type} name={field} validator={value => info.validator(value, action, values)} value={values[field]} onChange={e => setValue(field, e.target.value)}/>
                </label>
              );
            } else if (info.type === "checkbox") {
              return (
                <label key={field}>
                  <input type="checkbox" value={values[field]} onChange={e => setValue(field, e.target.checked)}/>
                  {name}
                </label>
              );
            } else {
              return null;
            }
          })}
        </form>
      </Dialog.Content>
      <Dialog.Footer>
        {buttonOrder.map(button => {
          const name = formData.buttons[button];
          if (!name) return null;
          return <button key={button} onClick={() => onClick(button)}>{name}</button>;
        })}
      </Dialog.Footer>
    </Dialog>
  );
}
